import { t } from '../i18n';
import type { RuntimeEnvironmentId } from '../spacetime/config';
export type LoginAction =
    | { action: 'login' | 'register'; username: string; password: string; environmentId: RuntimeEnvironmentId }
    | { action: 'cancel'; environmentId: RuntimeEnvironmentId };

export interface LoginPromptHandle {
    waitForAction(): Promise<LoginAction>;
    showError(message: string): void;
    close(): void;
}

export function showLoginPrompt(): LoginPromptHandle {
    const style = document.createElement('style');
    style.textContent = `
      #login-overlay {
        position: absolute;
        inset: 0;
        background: rgba(5, 6, 8, 0.95);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        backdrop-filter: blur(4px);
      }
      .login-form {
        background: #1d222c;
        border: 1px solid #2c3240;
        border-radius: 12px;
        padding: 32px;
        width: 320px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.5);
      }
      .login-form h2 { margin: 0 0 8px; text-align: center; color: #fff; font-size: 1.5rem; }
      .login-form input {
        background: #161a20;
        border: 1px solid #2c3444;
        color: #e0e7ff;
        padding: 12px;
        border-radius: 6px;
        font-size: 1rem;
        outline: none;
      }
      .login-form input:focus { border-color: #5ad1fa; }
      .login-form .environment-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
        color: #cbd5e1;
        font-size: 0.9rem;
      }
      .login-form .environment-options {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      }
      .login-form .environment-option {
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #2c3444;
        border-radius: 8px;
        background: #161a20;
        padding: 10px 12px;
      }
      .login-form .environment-option input { margin: 0; }
      .login-form .environment-option span { color: #e2e8f0; }
      .login-form .actions { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
      .login-form .actions-row { display: flex; gap: 8px; }
      .login-form button {
        flex: 1;
        padding: 12px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 600;
        font-size: 1rem;
        transition: all 0.2s;
      }
      .btn-primary { background: #5ad1fa; color: #05131d; }
      .btn-primary:hover { background: #74dcff; transform: translateY(-1px); }
      .btn-secondary { background: #2c3444; color: #e0e7ff; }
      .btn-secondary:hover { background: #3d4658; transform: translateY(-1px); }
      .btn-cancel { background: transparent; color: #94a3b8; border: 1px solid #2c3444; margin-top: 4px; }
      .btn-cancel:hover { color: #fff; border-color: #475569; }
      .login-error {
        color: #f87171;
        font-size: 0.9rem;
        min-height: 1.2rem;
      }
    `;
    document.head.appendChild(style);

    const overlay = document.createElement('div');
    overlay.id = 'login-overlay';
    overlay.innerHTML = `
    <form class="login-form" id="login-form">
      <h2>${t('login.title')}</h2>
      <input type="text" id="login-username" placeholder="${t('login.usernamePlaceholder')}" spellcheck="false" autocomplete="username">
      <input type="password" id="login-password" placeholder="${t('login.passwordPlaceholder')}" autocomplete="current-password">
      <div class="environment-group">
        <div>${t('login.environmentLabel')}</div>
        <div class="environment-options">
          <label class="environment-option">
            <input type="radio" name="runtime-environment" value="maincloud" checked>
            <span>${t('login.environmentMaincloud')}</span>
          </label>
          <label class="environment-option">
            <input type="radio" name="runtime-environment" value="local">
            <span>${t('login.environmentLocal')}</span>
          </label>
        </div>
      </div>
      <div id="login-error" class="login-error" hidden></div>
      <div class="actions">
        <div class="actions-row">
          <button type="submit" id="btn-login" class="btn-primary">${t('login.loginButton')}</button>
          <button type="button" id="btn-register" class="btn-secondary">${t('login.registerButton')}</button>
        </div>
        <button type="button" id="btn-cancel" class="btn-cancel">${t('login.cancelButton')}</button>
      </div>
    </form>
  `;
    document.body.appendChild(overlay);

    const loginForm = document.getElementById('login-form') as HTMLFormElement;
    const usernameInput = document.getElementById('login-username') as HTMLInputElement;
    const passwordInput = document.getElementById('login-password') as HTMLInputElement;
    const environmentInputs = Array.from(
        overlay.querySelectorAll<HTMLInputElement>('input[name="runtime-environment"]')
    );
    const loginButton = document.getElementById('btn-login') as HTMLButtonElement;
    const registerButton = document.getElementById('btn-register') as HTMLButtonElement;
    const cancelButton = document.getElementById('btn-cancel') as HTMLButtonElement;
    const errorElement = document.getElementById('login-error') as HTMLDivElement;

    const cleanup = (): void => {
        overlay.remove();
        style.remove();
    };

    const getInputs = () => ({
        username: usernameInput.value.trim(),
        password: passwordInput.value,
        environmentId:
            (environmentInputs.find((input) => input.checked)?.value as RuntimeEnvironmentId | undefined) ??
            'maincloud'
    });

    const actionQueue: LoginAction[] = [];
    let resolver: ((action: LoginAction) => void) | null = null;

    const pushAction = (action: LoginAction): void => {
        if (resolver) {
            const resolve = resolver;
            resolver = null;
            resolve(action);
        } else {
            actionQueue.push(action);
        }
    };

    const waitForAction = (): Promise<LoginAction> => {
        if (actionQueue.length > 0) {
            return Promise.resolve(actionQueue.shift()!);
        }
        return new Promise((resolve) => {
            resolver = resolve;
        });
    };

    const showError = (message: string): void => {
        if (!message) {
            errorElement.textContent = '';
            errorElement.hidden = true;
            return;
        }
        errorElement.textContent = message;
        errorElement.hidden = false;
    };

    const handleSubmit = (action: 'login' | 'register'): void => {
        const { username, password, environmentId } = getInputs();
        if (!username) {
            showError(t('login.usernameRequired'));
            usernameInput.focus();
            return;
        }
        showError('');
        pushAction({ action, username, password, environmentId });
    };

    loginForm.addEventListener('submit', (e) => {
        e.preventDefault();
        handleSubmit('login');
    });
    registerButton.addEventListener('click', () => handleSubmit('register'));
    cancelButton.addEventListener('click', () => {
        showError('');
        const environmentId =
            (environmentInputs.find((input) => input.checked)?.value as RuntimeEnvironmentId | undefined) ??
            'maincloud';
        pushAction({ action: 'cancel', environmentId });
    });

    // Focus username initially
    usernameInput.focus();

    return {
        waitForAction,
        showError,
        close: cleanup
    };
}
